import type { Ref } from 'react';
import React, { Children, cloneElement, useMemo, useRef } from 'react';

import { useUniqueId } from '../hooks/useUniqueId';
import { forkFn } from '../lib/forkFn';
import { mergeRefs } from '../lib/mergeRefs';
import { useTooltipState } from './hooks/useTooltipState';
import { enhance, Tooltip } from './Tooltip';
import type { TooltipStatefulBaseProps } from './Tooltip.types';

const OPEN_DELAY_ON_HOVER = 200;

/** Компонент используется для создания всплывающих подсказок */
export const TooltipStateful = enhance(
    ({
        children,
        content,
        defaultVisible = false,
        disabled,
        id: propId,
        trigger = 'hover',
        openDelay,
        closeDelay,
        contentRef,
        ...props
    }: TooltipStatefulBaseProps) => {
        const uniqId = useUniqueId(); //const uniqId = useUniqueId('tooltip');
        const id = propId || uniqId;
        const { visible, open, close, toggle } = useTooltipState({
            openDelay: trigger === 'hover' ? (openDelay ?? OPEN_DELAY_ON_HOVER) : openDelay,
            closeDelay,
            defaultVisible,
        });
        const anchorRef = useRef<HTMLElement>(null);

        const child = Children.only(children);

        // В дальнейшем данный код можно вынести куда-то выше и использовать по аналогии с Popup и Modal.
        const triggers = Array.isArray(trigger) ? trigger : [trigger];
        type Handlers = {
            onClick?: () => {};
            onFocus?: () => {};
            onBlur?: () => {};
            onMouseEnter?: () => {};
            onMouseLeave?: () => {};
        };
        const handlers = triggers.reduce<Handlers>((acc, trigger) => {
            switch (trigger) {
                case 'click':
                    acc.onClick = forkFn(child.props.onClick, () => toggle());
                    break;
                case 'focus':
                    acc.onFocus = forkFn(child.props.onFocus, () => open());
                    acc.onBlur = forkFn(child.props.onBlur, () => close());
                    break;
                case 'hover':
                    acc.onMouseEnter = forkFn(child.props.onMouseEnter, () => open());
                    acc.onMouseLeave = forkFn(child.props.onMouseLeave, () => close());
                    break;
            }
            return acc;
        }, {});

        // Определяем тип ссылки в зависимости от типа потомка,
        // нативные элементы реализуют ref, лего-компоненты должны реализовывать innerRef.
        const refKey = typeof child.type === 'string' ? 'ref' : 'innerRef';

        const ref = useMemo(() => {
            if (contentRef) {
                return contentRef;
            }

            let childRef: Ref<HTMLElement> | undefined;

            if (refKey === 'ref' && child && 'ref' in child) {
                // `ref` это специальный проп и его нет в `.props.`
                childRef = child.ref || child.props.ref;
            } else {
                childRef = child.props[refKey];
            }

            return !childRef ? anchorRef : mergeRefs(childRef, anchorRef);
        }, [refKey, child, contentRef]);

        return (
            <>
                {cloneElement(child, {
                    ...handlers,
                    [refKey]: ref,
                    ['aria-describedby']: id,
                })}
                <Tooltip
                    {...props}
                    id={id}
                    visible={disabled ? false : visible}
                    anchor={contentRef ?? anchorRef}
                    onClose={close}
                >
                    {content}
                </Tooltip>
            </>
        );
    },
);

export type TooltipStatefulProps = React.ComponentProps<typeof TooltipStateful>;
